import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { registerRequest } from '@/api/auth';
import i18n from '@/i18n';

const schema = z
  .object({
    email: z.string().email('Geçerli bir e-posta girin'),
    displayName: z.string().min(1, 'Ad soyad gerekli').max(80),
    password: z
      .string()
      .min(8, 'En az 8 karakter')
      .regex(/[A-Za-z]/, 'En az bir harf')
      .regex(/[0-9]/, 'En az bir rakam'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Parolalar eşleşmiyor',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAccessToken, setUser } = useAuthStore();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  async function onSubmit(data: FormData) {
    setServerError(null);
    setSubmitting(true);
    try {
      const { accessToken, user } = await registerRequest({
        email: data.email,
        displayName: data.displayName,
        password: data.password,
        locale: (i18n.language as 'tr' | 'en') || 'tr',
      });
      setAccessToken(accessToken);
      setUser(user);
      toast.success(t('auth.welcomeNew', { name: user.displayName }));
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setServerError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  // Used to pick locale. Imported at the top of the file.
  void i18n;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="card w-full max-w-md p-8">
        <h1 className="text-2xl font-bold mb-1">{t('auth.registerTitle')}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{t('auth.registerSubtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium mb-1">
              {t('auth.displayName')}
            </label>
            <input id="displayName" type="text" autoComplete="name" className="input" {...register('displayName')} />
            {errors.displayName && <p className="mt-1 text-xs text-red-600">{errors.displayName.message}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              {t('auth.email')}
            </label>
            <input id="email" type="email" autoComplete="email" className="input" {...register('email')} />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-1">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className="input"
              {...register('password')}
            />
            {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            <p className="mt-1 text-xs text-slate-500">{t('auth.passwordHint')}</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              className="input"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          {serverError && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {serverError}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? t('common.loading') : t('auth.register')}
          </button>

          <p className="text-sm text-center text-slate-500">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline">
              {t('auth.login')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
